import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { TOAST_STACK_GAP_PX } from "@/shared/ui/systemBannerStyles";

interface ToastSlot {
  id: string;
  height: number;
}

interface ToastStackActions {
  upsert: (id: string, height: number) => void;
  release: (id: string) => void;
}

// Actions live in their own context so their identity never changes: a toast's
// measuring effect must not re-run each time another toast moves.
const ToastSlotsContext = createContext<readonly ToastSlot[] | null>(null);
const ToastActionsContext = createContext<ToastStackActions | null>(null);

/**
 * Pixels a toast must rise so the newer toasts underneath it stay visible.
 * The last slot is the newest and stays on the corner (`0`).
 */
export function toastStackOffsetPx(
  slots: readonly { id: string; height: number }[],
  id: string,
  gapPx: number,
): number {
  const index = slots.findIndex((slot) => slot.id === id);
  if (index < 0) return 0;
  let offset = 0;
  for (let i = index + 1; i < slots.length; i += 1) {
    const height = slots[i].height;
    if (height <= 0) continue;
    offset += height + gapPx;
  }
  return offset;
}

function upsertSlot(slots: readonly ToastSlot[], id: string, height: number): readonly ToastSlot[] {
  const index = slots.findIndex((slot) => slot.id === id);
  if (index < 0) return [...slots, { id, height }];
  if (slots[index].height === height) return slots;
  const next = slots.slice();
  next[index] = { id, height };
  return next;
}

function releaseSlot(slots: readonly ToastSlot[], id: string): readonly ToastSlot[] {
  if (!slots.some((slot) => slot.id === id)) return slots;
  return slots.filter((slot) => slot.id !== id);
}

export function ToastStackProvider({ children }: Readonly<PropsWithChildren>) {
  const [slots, setSlots] = useState<readonly ToastSlot[]>([]);

  const actions = useMemo<ToastStackActions>(
    () => ({
      upsert: (id, height) => setSlots((current) => upsertSlot(current, id, height)),
      release: (id) => setSlots((current) => releaseSlot(current, id)),
    }),
    [],
  );

  return (
    <ToastActionsContext.Provider value={actions}>
      <ToastSlotsContext.Provider value={slots}>{children}</ToastSlotsContext.Provider>
    </ToastActionsContext.Provider>
  );
}

/** Offset for one open toast. Outside the provider every toast shares the corner. */
export function useToastStack(id: string): {
  offset: number;
  upsert: (height: number) => void;
  release: () => void;
} {
  const slots = useContext(ToastSlotsContext);
  const actions = useContext(ToastActionsContext);
  const offset = slots ? toastStackOffsetPx(slots, id, TOAST_STACK_GAP_PX) : 0;
  const upsert = useCallback(
    (height: number) => {
      actions?.upsert(id, height);
    },
    [actions, id],
  );
  const release = useCallback(() => {
    actions?.release(id);
  }, [actions, id]);
  return { offset, upsert, release };
}
